import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { LocaleFormatter, LocaleSettings } from '@/lib/LocaleFormatter';
import { getTimezoneFromBrowser, deriveCurrencyFromBrowserLocale } from '@/services/ipGeolocation';
import i18n, { isKeysMode } from '@/i18n/config';
import { matchBrowserLanguages, normalizeBrowserLocale, resolveI18nLocale } from '@/lib/localeResolver';
import { fetchSlugResolution } from '@/hooks/useSlugResolver';
import { resolveTwoSegmentImperative } from '@/hooks/useTwoSegmentResolver';
import { USER_LOCALE_STORAGE_KEY } from '@/constants/routing';
import { logger } from '@/utils/logger';
import { clearCrossDomainLocaleOverride, getCrossDomainLocaleOverride } from '@/lib/crossDomainState';
import { DOMAIN_DEFAULTS } from '@/lib/domainDefaults';
import { isPTLocale } from '@/constants/branding';

interface LocaleContextType {
  settings: LocaleSettings;
  updateSettings: (partial: Partial<LocaleSettings>) => Promise<void>;
  updateSettingsWithoutI18n: (partial: Partial<LocaleSettings>) => Promise<void>;
  isLoading: boolean;
  
  // Formatting helpers
  formatDate: (date: Date | string | number, style?: 'short' | 'medium' | 'long' | 'full') => string;
  formatTime: (date: Date | string | number) => string;
  formatDateTime: (date: Date | string | number, style?: 'short' | 'medium' | 'long') => string;
  formatRelativeTime: (date: Date | string | number) => string;
  formatRelativeTimeEnglish: (date: Date | string | number) => string;
  formatNumber: (num: number, options?: Intl.NumberFormatOptions) => string;
  formatCompactNumber: (num: number) => string;
  formatCurrency: (amount: number, currency?: string) => string;
  formatCompactCurrency: (amount: number, currency?: string) => string;
  formatPercent: (value: number, decimals?: number) => string;
  formatDuration: (ms: number) => string;
  formatDurationSeconds: (seconds: number) => string;
  formatBytes: (bytes: number) => string;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

// Default locale settings
const DEFAULT_SETTINGS: LocaleSettings = {
  locale: 'en-US',
  language: 'en',
  country: 'US',
  timezone: 'UTC',
  currency: 'USD',
  dateFormat: 'medium',
  numberFormat: 'standard',
  firstDayOfWeek: 0,
  isRTL: false,
  displayName: 'English (United States)',
};

// locale-data: bootstrap-locale-formatting - Static defaults for app initialization before DB loads
// Currency/timezone/RTL used for Intl formatting, not content localization
const LOCALE_CONFIGS: Record<string, Partial<LocaleSettings>> = {
  'en-US': { currency: 'USD', timezone: 'America/New_York', isRTL: false, displayName: 'English (United States)' },
  'en-GB': { currency: 'GBP', timezone: 'Europe/London', isRTL: false, displayName: 'English (United Kingdom)' },
  'es-MX': { currency: 'MXN', timezone: 'America/Mexico_City', isRTL: false, displayName: 'Español (México)' },
  'es-ES': { currency: 'EUR', timezone: 'Europe/Madrid', isRTL: false, displayName: 'Español (España)' },
  'pt-BR': { currency: 'BRL', timezone: 'America/Sao_Paulo', isRTL: false, displayName: 'Português (Brasil)' },
  'ar-SA': { currency: 'SAR', timezone: 'Asia/Riyadh', isRTL: true, displayName: 'العربية (السعودية)' },
  'ar-AE': { currency: 'AED', timezone: 'Asia/Dubai', isRTL: true, displayName: 'العربية (الإمارات)' },
  // Add more as needed
};

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<LocaleSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [formatter, setFormatter] = useState<LocaleFormatter>(new LocaleFormatter(DEFAULT_SETTINGS));

  // User data from database for locale building
  interface UserLocaleData {
    timezone?: string;
    currency_preference?: string;
    date_format_preference?: string;
    number_format_preference?: string;
    first_day_of_week?: number;
  }

  // Build locale settings from locale code
  const buildLocaleSettings = useCallback((locale: string, userData?: UserLocaleData): LocaleSettings => {
    const safeLocale = normalizeBrowserLocale(locale);
    const [language, country] = safeLocale.split('-');
    const config = LOCALE_CONFIGS[safeLocale] || {};
    
    // Detect timezone from browser if not provided
    const detectedTimezone = userData?.timezone || config.timezone || getTimezoneFromBrowser();

    return {
      locale: safeLocale,
      language: language || 'en',
      country: country || 'US',
      timezone: detectedTimezone,
      currency: userData?.currency_preference || config.currency || 'USD',
      dateFormat: userData?.date_format_preference || 'medium',
      numberFormat: userData?.number_format_preference || 'standard',
      firstDayOfWeek: userData?.first_day_of_week ?? 0,
      isRTL: config.isRTL || false,
      displayName: config.displayName || safeLocale,
    };
  }, []);

  // Detect locale based on priority (following Airbnb/LinkedIn standards)
  const detectLocale = useCallback(async (): Promise<LocaleSettings> => {
    const crossDomainLocale = getCrossDomainLocaleOverride();
    if (crossDomainLocale) {
      localStorage.setItem(USER_LOCALE_STORAGE_KEY, crossDomainLocale);
      clearCrossDomainLocaleOverride();
      return buildLocaleSettings(crossDomainLocale);
    }

    // 0. On tributario.com.br, domain is SOT — always pt-BR unless user explicitly selected a PT locale
    //    This prevents stale non-PT locales from tax.network/browser from overriding the domain
    //    and causing DomainEnforcer to immediately redirect away
    if (DOMAIN_DEFAULTS.isTributario) {
      const savedLocale = localStorage.getItem('user-locale');
      // Only respect stored locale if it's a PT variant (compatible with tributario domain)
      // Non-PT stored locales are stale cross-domain artifacts — ignore them
      if (savedLocale && isPTLocale(savedLocale)) {
        logger.debug('[LocaleContext] Tributario: using stored PT locale', { savedLocale });
        return buildLocaleSettings(savedLocale);
      }
      // No stored locale or non-PT stored locale — force domain default
      if (savedLocale && !isPTLocale(savedLocale)) {
        logger.info('[LocaleContext] Tributario: ignoring stale non-PT locale', { savedLocale });
      }
      localStorage.setItem('user-locale', DOMAIN_DEFAULTS.defaultLocale!);
      return buildLocaleSettings(DOMAIN_DEFAULTS.defaultLocale!);
    }

    // 1. Check user database settings (if authenticated) - HIGHEST PRIORITY
    //    DB is the source of truth for logged-in users (on non-tributario domains)
    if (user) {
      const { data } = await supabase
        .from('users')
        .select('locale, timezone, currency_preference, date_format_preference, number_format_preference, first_day_of_week')
        .eq('id', user.id)
        .single();
      
      if (data?.locale) {
        // Normalize DB locale (e.g. 'pt' → 'pt-BR') to match i18n canonical form
        const baseLang = data.locale.split('-')[0];
        const canonicalLocale = resolveI18nLocale(baseLang, navigator.language);
        localStorage.setItem('user-locale', canonicalLocale);
        return buildLocaleSettings(canonicalLocale, data);
      }
    }

    // 2. Check localStorage for previous explicit choice (ONLY for anonymous users)
    //    Skip for logged-in users - their locale comes from DB above
    if (!user) {
      const savedLocale = localStorage.getItem('user-locale');
      if (savedLocale) {
        return buildLocaleSettings(savedLocale);
      }
    }

    // 2.5. Domain default for non-tributario domains with a default locale
    //      (tributario is handled at step 0 above)
    if (!user && DOMAIN_DEFAULTS.defaultLocale) {
      localStorage.setItem('user-locale', DOMAIN_DEFAULTS.defaultLocale);
      return buildLocaleSettings(DOMAIN_DEFAULTS.defaultLocale);
    }

    // 3. Browser language preference - KEY: moved BEFORE IP detection
    //    Use navigator.languages array (not just single navigator.language)
    const browserPref = matchBrowserLanguages();
    if (browserPref) {
      // Store for future visits
      localStorage.setItem('user-locale', browserPref.i18nLocale);
      return buildLocaleSettings(browserPref.i18nLocale);
    }

    // 4. Browser-only formatting defaults (no external API call)
    //    Timezone from Intl API, currency from browser locale
    //    This eliminates IP geolocation calls for casual browsing
    const browserLang = navigator.language?.split('-')[0] || 'en';
    const i18nLocale = resolveI18nLocale(browserLang, navigator.language);
    
    localStorage.setItem('user-locale', i18nLocale);
    return buildLocaleSettings(i18nLocale, {
      timezone: getTimezoneFromBrowser(),
      currency_preference: deriveCurrencyFromBrowserLocale(navigator.language),
    });
  }, [user, buildLocaleSettings]);


  // Initialize locale settings - only run once on mount
  useEffect(() => {
    const initializeLocale = async () => {
      setIsLoading(true);
      logger.debug('[LocaleContext] initializeLocale starting', { pathname: window.location.pathname });
      try {
        // SLUG-AUTHORITATIVE ARCHITECTURE (INV-2, INV-3)
        // URL slug is ALWAYS the authority - check it first regardless of stored locale
        // This ensures /entdecken loads German even if user previously visited in English
        const pathname = window.location.pathname;
        const segments = pathname.split('/').filter(Boolean);
        
        // FAST PATH: Root URL "/" has no slug to resolve — skip slug resolution entirely
        if (segments.length === 0) {
          logger.debug('[LocaleContext] Fast path: root URL, skipping slug resolution');
          const localeSettings = await detectLocale();
          setSettings(localeSettings);
          setFormatter(new LocaleFormatter(localeSettings));
          document.documentElement.lang = localeSettings.language;
          document.documentElement.dir = localeSettings.isRTL ? 'rtl' : 'ltr';
          if (localeSettings.locale && i18n.language !== localeSettings.locale) {
            i18n.changeLanguage(localeSettings.locale);
          }
          setIsLoading(false);
          return;
        }
        
        if (segments.length > 0) {
          const slugToCheck = segments[0];
          const resolution = await fetchSlugResolution(slugToCheck);
          
          // For two-segment URLs where segment1 is a profile vanity URL (notFound or profile),
          // try resolving segment2 as the locale authority (expert-publication pattern)
          if (segments.length >= 2 && (!resolution?.found || resolution.sourceType === 'profile')) {
            const twoSegmentResolution = await resolveTwoSegmentImperative(segments[0], segments[1]);

            if (twoSegmentResolution.type === 'expert-publication' && twoSegmentResolution.locale) {
              // Publication slug determines locale for expert-publication URLs
              if (process.env.NODE_ENV === 'development') {
                logger.debug('[LocaleContext] Cold entry resolved from publication slug (two-segment)', {
                  locale: twoSegmentResolution.locale,
                  publicationSourceId: twoSegmentResolution.publicationSourceId,
                  vanityUrl: segments[0],
                  publicationSlug: segments[1],
                });
              }
              
              const localeSettings = buildLocaleSettings(twoSegmentResolution.locale);
              setSettings(localeSettings);
              setFormatter(new LocaleFormatter(localeSettings));
              
              localStorage.setItem(USER_LOCALE_STORAGE_KEY, twoSegmentResolution.locale);
              
              document.documentElement.lang = localeSettings.language;
              document.documentElement.dir = localeSettings.isRTL ? 'rtl' : 'ltr';
              
              if (localeSettings.locale && i18n.language !== localeSettings.locale) {
                i18n.changeLanguage(localeSettings.locale);
              }
              
              setIsLoading(false);
              return;
            }
          }
          
          if (resolution?.found) {
            // Skip slug-authority for functional routes — they are app UI, not content
            // The user's DB locale (via detectLocale) should take precedence
            const functionalRoutes = ['account', 'checkout', 'checkout-confirmation'];
            if (resolution.sourceType === 'route' && functionalRoutes.includes(resolution.sourceId)) {
              if (process.env.NODE_ENV === 'development') {
                logger.debug('[LocaleContext] Skipping slug-authority for account route, falling through to detectLocale');
              }
              // Fall through to detectLocale() below
            } else {
              // Slug-based resolution succeeded - use that locale (overrides stored)
              let resolvedLocale = resolution.locale;
              
              // On tributario.com.br, slug locale MUST be PT — override if not
              if (DOMAIN_DEFAULTS.isTributario && !isPTLocale(resolvedLocale)) {
                logger.info('[LocaleContext] Tributario: overriding non-PT slug locale', { 
                  from: resolvedLocale, to: 'pt-BR' 
                });
                resolvedLocale = 'pt-BR';
              }
              
              if (process.env.NODE_ENV === 'development') {
                logger.debug('[LocaleContext] Cold entry resolved from slug (authority)', {
                  locale: resolvedLocale,
                  entityType: resolution.sourceType,
                  entitySourceId: resolution.sourceId,
                });
              }
              
              const localeSettings = buildLocaleSettings(resolvedLocale);
              setSettings(localeSettings);
              setFormatter(new LocaleFormatter(localeSettings));
              
              // Store for future visits
              localStorage.setItem(USER_LOCALE_STORAGE_KEY, resolution.locale);
              
              // Update HTML attributes
              document.documentElement.lang = localeSettings.language;
              document.documentElement.dir = localeSettings.isRTL ? 'rtl' : 'ltr';
              
              // Update i18n language
              if (localeSettings.locale && i18n.language !== localeSettings.locale) {
                i18n.changeLanguage(localeSettings.locale);
              }
              
              setIsLoading(false);
              return;
            }
          }
        }
        
        // Domain default: tributario.com.br defaults to pt-BR for first-time visitors
        // But stored user preferences are respected — users can switch away

        // Existing flow: detectLocale() for stored/browser/IP detection
        const localeSettings = await detectLocale();
        
        setSettings(localeSettings);
        setFormatter(new LocaleFormatter(localeSettings));
        
        // Update HTML attributes
        document.documentElement.lang = localeSettings.language;
        document.documentElement.dir = localeSettings.isRTL ? 'rtl' : 'ltr';
        
        // Update i18n language - use full locale code (e.g., 'en-US')
        // Keys debug mode no longer blocks language changes
        if (localeSettings.locale && i18n.language !== localeSettings.locale) {
          i18n.changeLanguage(localeSettings.locale);
        }
      } catch (err) {
        // Error initializing locale - use defaults
        if (process.env.NODE_ENV === 'development') {
          logger.error('[LocaleContext] Initialization failed', { error: err });
        }
        setSettings(DEFAULT_SETTINGS);
        setFormatter(new LocaleFormatter(DEFAULT_SETTINGS));
      } finally {
        setIsLoading(false);
      }
    };

    initializeLocale();
  }, [user?.id, detectLocale, buildLocaleSettings]); // Re-run when user logs in/out

  // Update locale settings (includes i18n.changeLanguage)
  const updateSettings = useCallback(async (partial: Partial<LocaleSettings>) => {
    // On tributario.com.br, allow locale changes (branding switches dynamically)
    // No longer blocking non-PT locales — user can switch language freely

    const newSettings = { ...settings, ...partial };
    setSettings(newSettings);
    setFormatter(new LocaleFormatter(newSettings));

    // Save to localStorage
    localStorage.setItem('user-locale', newSettings.locale);

    // Update HTML attributes
    document.documentElement.lang = newSettings.language;
    document.documentElement.dir = newSettings.isRTL ? 'rtl' : 'ltr';

    // Update i18n language - use full locale code (e.g., 'en-US')
    if (newSettings.locale && i18n.language !== newSettings.locale) {
      await i18n.changeLanguage(newSettings.locale);
    }

    // Save to database if authenticated
    if (user) {
      try {
        await supabase
          .from('users')
          .update({
            locale: newSettings.locale,
            timezone: newSettings.timezone,
            currency_preference: newSettings.currency,
            date_format_preference: newSettings.dateFormat,
            number_format_preference: newSettings.numberFormat,
            first_day_of_week: newSettings.firstDayOfWeek,
          })
          .eq('id', user.id);
      } catch {
        // Error saving locale settings - continue with local state
      }
    }
  }, [settings, user]);

  // Update locale settings WITHOUT triggering i18n.changeLanguage
  // Used by useLanguageChange hook to prevent race conditions
  const updateSettingsWithoutI18n = useCallback(async (partial: Partial<LocaleSettings>) => {
    // Keys debug mode no longer blocks language changes

    const newSettings = { ...settings, ...partial };
    setSettings(newSettings);
    setFormatter(new LocaleFormatter(newSettings));

    // Save to localStorage
    localStorage.setItem('user-locale', newSettings.locale);

    // Update HTML attributes
    document.documentElement.lang = newSettings.language;
    document.documentElement.dir = newSettings.isRTL ? 'rtl' : 'ltr';

    // NOTE: i18n.changeLanguage is NOT called here - the useLanguageChange hook handles it
    // after URL navigation completes to prevent race conditions

    // Save to database if authenticated
    if (user) {
      try {
        await supabase
          .from('users')
          .update({
            locale: newSettings.locale,
            timezone: newSettings.timezone,
            currency_preference: newSettings.currency,
            date_format_preference: newSettings.dateFormat,
            number_format_preference: newSettings.numberFormat,
            first_day_of_week: newSettings.firstDayOfWeek,
          })
          .eq('id', user.id);
      } catch {
        // Error saving locale settings - continue with local state
      }
    }
  }, [settings, user]);

  // Formatting helper methods
  const formatDate = useCallback((date: Date | string | number, style?: 'short' | 'medium' | 'long' | 'full') => {
    return formatter.formatDate(date, style);
  }, [formatter]);

  const formatTime = useCallback((date: Date | string | number) => {
    return formatter.formatTime(date);
  }, [formatter]);

  const formatDateTime = useCallback((date: Date | string | number, style?: 'short' | 'medium' | 'long') => {
    return formatter.formatDateTime(date, style);
  }, [formatter]);

  const formatRelativeTime = useCallback((date: Date | string | number) => {
    return formatter.formatRelativeTime(date);
  }, [formatter]);

  const formatRelativeTimeEnglish = useCallback((date: Date | string | number) => {
    return formatter.formatRelativeTimeEnglish(date);
  }, [formatter]);

  const formatNumber = useCallback((num: number, options?: Intl.NumberFormatOptions) => {
    return formatter.formatNumber(num, options);
  }, [formatter]);

  const formatCompactNumber = useCallback((num: number) => {
    return formatter.formatCompactNumber(num);
  }, [formatter]);

  const formatCurrency = useCallback((amount: number, currency?: string) => {
    return formatter.formatCurrency(amount, currency);
  }, [formatter]);

  const formatCompactCurrency = useCallback((amount: number, currency?: string) => {
    return formatter.formatCompactCurrency(amount, currency);
  }, [formatter]);

  const formatPercent = useCallback((value: number, decimals?: number) => {
    return formatter.formatPercent(value, decimals);
  }, [formatter]);

  const formatDuration = useCallback((ms: number) => {
    return formatter.formatDuration(ms);
  }, [formatter]);

  const formatDurationSeconds = useCallback((seconds: number) => {
    return formatter.formatDurationSeconds(seconds);
  }, [formatter]);

  const formatBytes = useCallback((bytes: number) => {
    return formatter.formatBytes(bytes);
  }, [formatter]);

  return (
    <LocaleContext.Provider
      value={{
        settings,
        updateSettings,
        updateSettingsWithoutI18n,
        isLoading,
        formatDate,
        formatTime,
        formatDateTime,
        formatRelativeTime,
        formatRelativeTimeEnglish,
        formatNumber,
        formatCompactNumber,
        formatCurrency,
        formatCompactCurrency,
        formatPercent,
        formatDuration,
        formatDurationSeconds,
        formatBytes,
      }}
    >
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = (): LocaleContextType => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};
